import { useEffect, useState } from "react";
import {
    Box, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
    Divider, IconButton, Radio, Stack, Typography
} from "@mui/material";
import { Close, CopyAllOutlined, Edit, History as HistoryIcon, Restore, Settings } from "@mui/icons-material";
import { green, grey } from "@mui/material/colors";
import { useCommonItems } from "../../../../../contexts/CommonItensProvider";
import { carregaHistoricos } from "../configs/functions";
import { snapshotToNormalized } from "../configs/layoutAdapters";
import { EquipmentLayoutPreview } from "./EquipmentLayoutPreview";

const ORIGEM_META = {
    store: { label: 'Criação', color: 'success', Icon: Settings },
    update: { label: 'Edição', color: 'info', Icon: Edit },
    copia: { label: 'Cópia', color: 'warning', Icon: CopyAllOutlined },
    restauracao: { label: 'Restauração', color: 'default', Icon: Restore },
};

export function EquipmentHistoryDialog({ open, onClose, equipamento, tipo }) {
    const { exibirAlerta } = useCommonItems();

    const isArmario = tipo === 'armario';

    const [loading, setLoading] = useState(false);
    const [historicos, setHistoricos] = useState([]);
    const [selecionado, setSelecionado] = useState(null);

    const colsGrid = isArmario
        ? (equipamento?.numeroColunas ?? equipamento?.numero_colunas ?? 5)
        : (equipamento?.qtd_motores === 60 ? 10 : 6);

    const load = async () => {
        if (!equipamento?.id) return;

        setLoading(true);
        try {
            const { data } = await carregaHistoricos({
                id_empresas: [equipamento.empresa?.id ?? equipamento.id_empresa],
                id_equipamento: equipamento.id,
                tipo_equipamento: tipo,
                detalhado: 1,
                limit: 20,
            });

            const lista = data?.data ?? [];
            setHistoricos(lista);
            setSelecionado(lista[0] ?? null);
        } catch (error) {
            exibirAlerta('Erro ao carregar histórico', error?.response?.data?.error || '', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            load();
        } else {
            setHistoricos([]);
            setSelecionado(null);
        }
    }, [open, equipamento?.id]);

    const labelTipo = {
        maquina: 'Máquina', balcao: 'Balcão', virtual: 'Balcão Virtual', armario: 'Armário',
    }[tipo] ?? 'Equipamento';

    const layout = selecionado
        ? snapshotToNormalized(
            selecionado.configuracoes,
            isArmario ? 'armario' : 'maquina',
            colsGrid
        )
        : null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
                <HistoryIcon fontSize="small" />
                Histórico de configurações
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                    — {equipamento?.nome}
                </Typography>

                <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : historicos.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                        Nenhum histórico registrado para esta {labelTipo.toLowerCase()}.
                    </Typography>
                ) : (
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <Stack spacing={1} sx={{ flex: '0 0 200px', maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
                            {historicos.map((h) => {
                                const meta = ORIGEM_META[h.origem] ?? { label: h.origem, color: 'default', Icon: HistoryIcon };
                                const sel = selecionado?.id === h.id;
                                const { Icon } = meta;

                                return (
                                    <Box
                                        key={h.id}
                                        onClick={() => setSelecionado(h)}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
                                            borderRadius: 2, cursor: 'pointer', border: '1px solid',
                                            borderColor: sel ? 'primary.main' : grey[300],
                                            backgroundColor: sel ? green[50] : 'transparent',
                                            transition: 'all 0.15s ease',
                                            '&:hover': {
                                                borderColor: sel ? 'primary.main' : grey[400],
                                                backgroundColor: sel ? green[50] : grey[50],
                                            },
                                        }}
                                    >
                                        <Radio checked={sel} size="small" tabIndex={-1} sx={{ p: 0 }} />

                                        <Box sx={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                                            backgroundColor: sel ? 'primary.main' : grey[100],
                                            color: sel ? '#fff' : grey[600],
                                        }}>
                                            <Icon fontSize="small" />
                                        </Box>

                                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={600} noWrap>
                                                {h.data}
                                            </Typography>
                                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
                                                <Settings sx={{ fontSize: 13 }} />
                                                <Typography variant="caption">
                                                    {h.qtdConfigs ?? 0} {isArmario
                                                    ? (h.qtdConfigs === 1 ? 'porta' : 'portas')
                                                    : (h.qtdConfigs === 1 ? 'motor' : 'motores')}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Stack>

                        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

                        <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
                            {selecionado ? (
                                <>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                        <Typography variant="subtitle2">{selecionado.data}</Typography>
                                        {selecionado.pessoa?.nome && (
                                            <Typography variant="caption" color="text.secondary">
                                                · por {selecionado.pessoa.nome}
                                            </Typography>
                                        )}
                                    </Stack>

                                    <EquipmentLayoutPreview layout={layout} />
                                </>
                            ) : (
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                                    Selecione uma versão para visualizar.
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    );
}